import React, { useState } from 'react';
import {
  Container,
  Box,
  alpha,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { Menu } from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import DocsSidebar from '../components/Documentation/DocsSidebar';

// Import all tab components
import FrontendOverviewTab from '../components/Documentation/tabs/FrontendOverviewTab';
import FrontendFeaturesTab from '../components/Documentation/tabs/FrontendFeaturesTab';
import FrontendTechStackTab from '../components/Documentation/tabs/FrontendTechStackTab';
import FrontendInstallationTab from '../components/Documentation/tabs/FrontendInstallationTab';
import FrontendStructureTab from '../components/Documentation/tabs/FrontendStructureTab';
import FrontendUIUXTab from '../components/Documentation/tabs/FrontendUIUXTab';
import FrontendSecurityTab from '../components/Documentation/tabs/FrontendSecurityTab';
import FrontendPerformanceTab from '../components/Documentation/tabs/FrontendPerformanceTab';
import BackendOverviewTab from '../components/Documentation/tabs/BackendOverviewTab';
import BackendTechStackTab from '../components/Documentation/tabs/BackendTechStackTab';
import BackendInstallationTab from '../components/Documentation/tabs/BackendInstallationTab';
import BackendAPIEndpointsTab from '../components/Documentation/tabs/BackendAPIEndpointTab';
import BackendWebSocketTab from '../components/Documentation/tabs/BackendWebSocketTab';
import BackendStructureTab from '../components/Documentation/tabs/BackendStructureTab';
import BackendSecurityTab from '../components/Documentation/tabs/BackendSecurityTab';
import BackendDeploymentTab from '../components/Documentation/tabs/BackendDeploymentTab';
import SearchResultsTab from '../components/Documentation/tabs/SearchResultsTab';

import TextLogo from '../assets/Text-WFHCafe_WObg.png';

interface DocsPageProps {}

const DocsPage: React.FC<DocsPageProps> = () => {
  const [activeTab, setActiveTab] = useState('frontend-overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  
  const { mode } = useTheme();
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const isDark = mode === 'dark';

  // --- LOKI THEME PALETTE ---
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lightGreen = '#0F3B2E';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 50%, #1D5C4A 100%)';
  
  const colors = {
    sidebarBg: isDark ? 'rgba(5, 24, 20, 0.85)' : 'rgba(255, 255, 255, 0.9)',
    pageBg: isDark ? '#000000' : '#FAFAFA',
    cardBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.6)',
    textPrimary: isDark ? '#FFFFFF' : '#051814',
    textSecondary: isDark ? alpha('#FFFFFF', 0.6) : alpha('#051814', 0.6),
    accent: goldColor,
    danger: '#DA373C',
    success: '#23A559',
    iconColor: isDark ? goldColor : lightGreen,
    headerBg: isDark ? 'rgba(0,0,0,0.3)' : '#E3E5E8',
    borderColor: isDark ? alpha(goldColor, 0.2) : alpha(deepGreen, 0.1)
  };

  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassStyle = {
    backdropFilter: 'blur(20px)',
    border: `1px solid ${colors.borderColor}`,
    boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.3)' : '0 4px 20px rgba(0,0,0,0.05)',
  };

  // Common props for all tabs
  const commonProps = {
    isDark,
    colors,
    discordFont,
    glassStyle,
    lokiGradient,
    goldColor,
    isMobile,
  };

  const renderContent = () => {
    if (searchQuery) {
      return (
        <SearchResultsTab 
          {...commonProps}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      );
    }

    switch (activeTab) {
      // Frontend Tabs
      case 'frontend-overview':
        return <FrontendOverviewTab {...commonProps} />;
      case 'frontend-features':
        return <FrontendFeaturesTab {...commonProps} />;
      case 'frontend-tech-stack':
        return <FrontendTechStackTab {...commonProps} />;
      case 'frontend-installation':
        return <FrontendInstallationTab {...commonProps} />;
      case 'frontend-structure':
        return <FrontendStructureTab {...commonProps} />;
      case 'frontend-ui-ux':
        return <FrontendUIUXTab {...commonProps} />;
      case 'frontend-security':
        return <FrontendSecurityTab {...commonProps} />;
      case 'frontend-performance':
        return <FrontendPerformanceTab {...commonProps} />;
      
      // Backend Tabs
      case 'backend-overview':
        return <BackendOverviewTab {...commonProps} />;
      case 'backend-tech-stack':
        return <BackendTechStackTab {...commonProps} />;
      case 'backend-installation':
        return <BackendInstallationTab {...commonProps} />;
      case 'backend-api-endpoints':
        return <BackendAPIEndpointsTab {...commonProps} />;
      case 'backend-websocket':
        return <BackendWebSocketTab {...commonProps} />;
      case 'backend-structure':
        return <BackendStructureTab {...commonProps} />;
      case 'backend-security':
        return <BackendSecurityTab {...commonProps} />;
      case 'backend-deployment':
        return <BackendDeploymentTab {...commonProps} />;
      
      default:
        return <FrontendOverviewTab {...commonProps} />;
    }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      minHeight: 'calc(100vh - 64px)', 
      bgcolor: colors.pageBg, 
      fontFamily: 'Inter, sans-serif' 
    }}>
      
      {/* Sidebar */}
      <DocsSidebar 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        colors={colors}
        isDark={isDark}
        goldColor={goldColor}
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      {/* MAIN CONTENT AREA */}
      <Box sx={{ 
        flex: 1, 
        overflowY: 'auto', 
        height: 'calc(100vh - 64px)', 
        scrollbarWidth: 'thin',
        width: '100%'
      }}>
        {/* Mobile Header */}
        <Box sx={{ 
          display: { xs: 'flex', md: 'none' }, 
          alignItems: 'center', 
          justifyContent: 'space-between',
          pt: 2,
          px: 2,
          backdropFilter: 'blur(20px)',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          bgcolor: colors.pageBg,
          borderBottom: `1px solid ${colors.borderColor}`
        }}>
          <IconButton
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            sx={{ 
              color: colors.textPrimary,
              '&:hover': { bgcolor: alpha(colors.textSecondary, 0.1) }
            }}
          >
            <Menu />
          </IconButton>
          <Box
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            component="img"
            src={TextLogo}
            alt="Documentation"
            sx={{
              height: 45,           
              width: 'auto',
            }}
          />
        </Box>
        
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5, lg: 6 }, px: { xs: 2, sm: 3 } }}>
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default DocsPage;